import React from "react";
import { cn } from "@/lib/utils";
import { VersionComparisonProps } from "./types";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  X,
  GitCompare,
  ArrowRight,
  Calendar,
  User,
  Plus,
  Minus,
  Edit,
  Move,
  AlertCircle,
  GitMerge,
} from "lucide-react";
import { format } from "date-fns";

const getChangeIcon = (type: string) => {
  switch (type) {
    case "create":
      return <Plus className="w-3 h-3 text-green-500" />;
    case "update":
      return <Edit className="w-3 h-3 text-blue-500" />;
    case "delete":
      return <Minus className="w-3 h-3 text-red-500" />;
    case "move":
      return <Move className="w-3 h-3 text-purple-500" />;
    default:
      return <AlertCircle className="w-3 h-3 text-muted-foreground" />;
  }
};

export const VersionComparison: React.FC<VersionComparisonProps> = ({
  baseVersion,
  targetVersion,
  onClose,
  onMerge,
}) => {
  // Calculate differences between versions
  const baseDiff = baseVersion.changes || [];
  const targetDiff = targetVersion.changes || [];

  // For this example, we'll show both version's changes
  // In a real implementation, you'd calculate actual differences
  const allChanges = [
    ...baseDiff.map((change) => ({ ...change, source: "base" as const })),
    ...targetDiff.map((change) => ({ ...change, source: "target" as const })),
  ].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

  const changeStats = {
    base: baseDiff.length,
    target: targetDiff.length,
    total: allChanges.length,
  };

  const renderVersionCard = (
    version: typeof baseVersion,
    label: string,
    isTarget: boolean = false,
  ) => (
    <Card className={cn("flex-1", isTarget && "border-primary")}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center space-x-2">
            <div
              className={cn(
                "w-3 h-3 rounded-full",
                version.isPublished ? "bg-blue-500" : "bg-orange-500",
              )}
            />
            <span>{label}</span>
          </CardTitle>
          <Badge variant={version.isPublished ? "default" : "outline"}>
            v{version.version}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        <div className="space-y-3">
          {/* Version Info */}
          <div>
            {version.name && (
              <h4 className="font-medium text-sm mb-1">{version.name}</h4>
            )}
            {version.description && (
              <p className="text-sm text-muted-foreground line-clamp-2">
                {version.description}
              </p>
            )}
          </div>

          {/* Metadata */}
          <div className="space-y-2 text-sm">
            <div className="flex items-center space-x-2">
              <User className="w-3 h-3 text-muted-foreground" />
              <span className="text-muted-foreground">By</span>
              <span>{version.createdBy}</span>
            </div>

            <div className="flex items-center space-x-2">
              <Calendar className="w-3 h-3 text-muted-foreground" />
              <span className="text-muted-foreground">Created</span>
              <span>{format(version.createdAt, "PPp")}</span>
            </div>
          </div>

          {/* Tags */}
          {version.tags && version.tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {version.tags.slice(0, 3).map((tag) => (
                <Badge key={tag} variant="outline" className="text-xs">
                  {tag}
                </Badge>
              ))}
              {version.tags.length > 3 && (
                <Badge variant="outline" className="text-xs">
                  +{version.tags.length - 3}
                </Badge>
              )}
            </div>
          )}

          {/* Change count */}
          <div className="pt-2 border-t">
            <span className="text-sm text-muted-foreground">
              {version.changes?.length || 0} changes
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-6xl max-h-[90vh] p-0">
        <DialogHeader className="p-6 pb-4">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-xl font-bold flex items-center space-x-2">
              <GitCompare className="w-5 h-5" />
              <span>Compare Versions</span>
            </DialogTitle>

            <DialogClose asChild>
              <Button variant="outline" size="sm">
                <X className="w-4 h-4" />
              </Button>
            </DialogClose>
          </div>
        </DialogHeader>

        <ScrollArea className="flex-1 px-6">
          <div className="space-y-6 pb-6">
            {/* Version Comparison Header */}
            <div className="flex items-center space-x-4">
              {renderVersionCard(baseVersion, "Base Version")}

              <div className="flex-shrink-0 flex flex-col items-center space-y-2">
                <ArrowRight className="w-6 h-6 text-muted-foreground" />
                <Badge variant="outline" className="text-xs">
                  Compare
                </Badge>
              </div>

              {renderVersionCard(targetVersion, "Target Version", true)}
            </div>

            {/* Comparison Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Comparison Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-muted/30 rounded-lg">
                    <div className="text-2xl font-bold">{changeStats.base}</div>
                    <div className="text-sm text-muted-foreground">
                      Base Changes
                    </div>
                  </div>

                  <div className="text-center p-4 bg-muted/30 rounded-lg">
                    <div className="text-2xl font-bold">
                      {changeStats.target}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Target Changes
                    </div>
                  </div>

                  <div className="text-center p-4 bg-primary/10 rounded-lg">
                    <div className="text-2xl font-bold">
                      {changeStats.total}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Total Changes
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Changes Timeline */}
            {allChanges.length > 0 ? (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Changes Timeline</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {allChanges.map((change, index) => (
                      <div
                        key={`${change.source}-${change.id || index}`}
                        className="flex items-start space-x-3 p-3 rounded-md bg-muted/30"
                      >
                        <div className="flex-shrink-0 mt-0.5">
                          {getChangeIcon(change.type)}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="text-sm font-medium capitalize">
                              {change.type}
                            </span>
                            <Badge
                              variant="outline"
                              className="text-xs capitalize"
                            >
                              {change.target}
                            </Badge>
                            <Badge
                              variant={
                                change.source === "base"
                                  ? "secondary"
                                  : "default"
                              }
                              className="text-xs"
                            >
                              v
                              {change.source === "base"
                                ? baseVersion.version
                                : targetVersion.version}
                            </Badge>
                          </div>

                          {change.description && (
                            <p className="text-sm text-muted-foreground mb-2">
                              {change.description}
                            </p>
                          )}

                          <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                            <Calendar className="w-3 h-3" />
                            <span>{format(change.timestamp, "PPp")}</span>
                            {change.userId && (
                              <>
                                <span>•</span>
                                <span>{change.userId}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="flex items-center justify-center py-8">
                  <div className="text-center">
                    <AlertCircle className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">
                      No changes to compare between these versions
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </ScrollArea>

        {/* Actions Footer */}
        <div className="p-6 pt-0 border-t">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Comparing v{baseVersion.version} with v{targetVersion.version}
            </div>

            <div className="flex items-center space-x-2">
              {onMerge && (
                <Button
                  onClick={onMerge}
                  className="flex items-center space-x-2"
                >
                  <GitMerge className="w-4 h-4" />
                  <span>Merge Changes</span>
                </Button>
              )}

              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VersionComparison;
